import { useCallback, useEffect, useRef, useState } from 'react';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';

// https://github.com/mantinedev/mantine/blob/master/packages/%40mantine/hooks/src/use-debounced-value/use-debounced-value.ts

export function useDebouncedValue<T>(
    value: T,
    wait: number = DEBOUNCE_TIME,
    options = { leading: false },
) {
    const [_value, setValue] = useState(value);
    const mountedRef = useRef(false);
    const timeoutRef = useRef<number | null>(null);
    const cooldownRef = useRef(false);

    const cancel = useCallback((): void => {
        if (typeof timeoutRef.current === 'number') {
            window.clearTimeout(timeoutRef.current);
        }
    }, []);

    useEffect(() => {
        if (mountedRef.current) {
            if (!cooldownRef.current && options.leading) {
                cooldownRef.current = true;
                setValue(value);
            } else {
                cancel();
                timeoutRef.current = window.setTimeout(() => {
                    cooldownRef.current = false;
                    setValue(value);
                }, wait);
            }
        }
    }, [value, options.leading, wait, cancel]);

    useEffect(() => {
        mountedRef.current = true;
        return cancel;
    }, [cancel]);

    return [_value, cancel] as const;
}
